/**
 * Document Helper - utilities for document display and formatting
 */

// Constants for document processing
const PDF_PREFIX = 'PDF:';
const TIMESTAMP_PATTERN = /_\d{14}(?=\.pdf$)/; // Format: _YYYYMMDDHHMMSS before .pdf extension

/**
 * Checks if a document is a PDF based on its external link
 * @param {string} externalLink - The external link string
 * @returns {boolean} True if the document is a PDF
 */
export const isPdfDocument = externalLink => {
  if (!externalLink) return false;
  return externalLink.startsWith(PDF_PREFIX);
};

/**
 * Whether the document points at something a browser can actually open.
 *
 * The API's `display_url` is the blob URL for an uploaded PDF and the link
 * itself for a URL document, but for an imported PDF — one inserted with the
 * "PDF: name" convention and no attached file — it falls back to that same
 * label, which is not a URL. Offering to open it would 404.
 *
 * @param {string} displayUrl - The document's display_url
 * @returns {boolean} True when the URL can be opened
 */
export const hasViewableFile = displayUrl =>
  /^https?:\/\//i.test(displayUrl || '');

/**
 * Formats the display link for documents
 * For PDF documents: removes 'PDF:' prefix and timestamp suffix
 * For regular URLs: returns as-is
 *
 * @param {string} externalLink - The external link string
 * @returns {string} Formatted display link
 */
export const formatDocumentLink = externalLink => {
  if (!externalLink) return '';

  if (isPdfDocument(externalLink)) {
    // Remove 'PDF:' prefix
    const fullName = externalLink.substring(PDF_PREFIX.length);
    // Remove timestamp suffix if present
    return fullName.replace(TIMESTAMP_PATTERN, '');
  }

  return externalLink;
};
